/**
 * useSweepPurchase Hook
 *
 * State machine for sequential NFT purchases in sweep mode.
 * Handles creating Xaman payloads and tracking purchase progress.
 */

import { useState, useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { apiClient } from '../api/client';
import type { NFT, Offer } from '../types';
import type { SigningPayload } from '../types/signing';
import { toSigningPayload } from '../lib/wallet';
import { isSupportedMarketplace, getMarketplaceFeePercentage, calculateBuyAmount } from '../lib/marketplace-constants';

export type SweepStage = 'idle' | 'confirming' | 'purchasing' | 'complete';

export interface PurchaseFailure {
  tokenId: string;
  name: string;
  error: string;
}

export interface SweepPurchaseState {
  stage: SweepStage;
  currentIndex: number;
  totalCount: number;
  // Normalized signing payload — Xaman (QR/WS) or client-signing (joey-wc).
  currentPayload: SigningPayload | null;
  currentNft: NFT | null;
  completedPurchases: string[]; // NFT token IDs
  failedPurchases: PurchaseFailure[];
  cancelled: boolean;
}

interface SweepTotals {
  subtotal: number;  // XRP (not drops)
  fees: number;      // XRP (not drops)
  total: number;     // XRP (not drops)
}

/**
 * Get the lowest valid sell offer from an NFT's active_offers
 */
function getLowestSellOffer(activeOffers: Offer[] | undefined): Offer | null {
  if (!activeOffers || activeOffers.length === 0) return null;

  const now = Math.floor(Date.now() / 1000);

  // Filter out expired offers and sort by price (lowest first)
  const validOffers = activeOffers
    .filter(offer => !offer.expiration || offer.expiration > now)
    .sort((a, b) => a.price - b.price);

  return validOffers[0] || null;
}

/**
 * Check if an offer is marketplace-brokered (any supported marketplace)
 */
function isMarketplaceBrokered(offer: Offer): boolean {
  return !!offer.destination && isSupportedMarketplace(offer.destination);
}

/**
 * Calculate total cost for sweep including fees
 */
export function calculateSweepTotals(nfts: NFT[]): SweepTotals {
  let subtotal = 0;
  let fees = 0;

  for (const nft of nfts) {
    const offer = getLowestSellOffer(nft.active_offers);
    if (!offer) continue;

    // Convert to number in case API returns string
    const price = Number(offer.price) || 0;
    subtotal += price;

    if (isMarketplaceBrokered(offer)) {
      // Get fee percentage for the specific marketplace
      const feePercentage = getMarketplaceFeePercentage(offer.destination!) || 1.0;
      fees += price * (feePercentage / 100);
    }
  }

  return {
    subtotal,
    fees,
    total: subtotal + fees,
  };
}

const initialState: SweepPurchaseState = {
  stage: 'idle',
  currentIndex: 0,
  totalCount: 0,
  currentPayload: null,
  currentNft: null,
  completedPurchases: [],
  failedPurchases: [],
  cancelled: false,
};

export function useSweepPurchase() {
  const { user } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  const [state, setState] = useState<SweepPurchaseState>(initialState);
  const [nftsToSweep, setNftsToSweep] = useState<NFT[]>([]);

  /**
   * Start the confirmation stage with selected NFTs
   */
  const startConfirmation = useCallback((nfts: NFT[]) => {
    setNftsToSweep(nfts);
    setState({
      ...initialState,
      stage: 'confirming',
      totalCount: nfts.length,
    });
  }, []);

  /**
   * Create payload for a specific NFT purchase
   */
  const createPurchasePayload = useCallback(async (nft: NFT): Promise<SigningPayload | null> => {
    if (!user?.xrpl_address) return null;

    const offer = getLowestSellOffer(nft.active_offers);
    if (!offer) {
      throw new Error('No valid sell offer found');
    }

    const isBrokered = isMarketplaceBrokered(offer);

    try {
      if (isBrokered) {
        // For marketplace-brokered offers, create a matching buy offer (same as cart checkout)
        // The marketplace broker will then match both offers
        const priceDrops = Math.round(offer.price * 1_000_000);
        const buyOfferAmount = calculateBuyAmount(priceDrops, offer.destination!);
        const response = await apiClient.post<any>('/offers/create', {
          nftTokenId: nft.nft_token_id,
          offerType: 'buy',
          amount: buyOfferAmount,
          owner: nft.owner_address,
          userAddress: user.xrpl_address,
          destination: offer.destination,
        });

        if (response.success && (response.data?.signingIntent || response.data?.payload)) {
          return toSigningPayload(response.data.signingIntent ?? response.data.payload);
        }
        throw new Error(response.error || 'Failed to create payload');
      } else {
        // For P2P offers, use /offers/accept-p2p endpoint
        const response = await apiClient.post<any>('/offers/accept-p2p', {
          nftTokenId: nft.nft_token_id,
          offerIndex: offer.offer_id,
          userAddress: user.xrpl_address,
          offerType: 'sell',
        });

        if (response.success && (response.data?.signingIntent || response.data?.payload)) {
          return toSigningPayload(response.data.signingIntent ?? response.data.payload);
        }
        throw new Error(response.error || 'Failed to create payload');
      }
    } catch (err: any) {
      throw new Error(err.message || 'Failed to create purchase payload');
    }
  }, [user?.xrpl_address]);

  /**
   * Start purchasing - create payload for first NFT
   */
  const startPurchasing = useCallback(async () => {
    if (nftsToSweep.length === 0) return;

    setState(s => ({
      ...s,
      stage: 'purchasing',
      currentIndex: 0,
      cancelled: false,
    }));

    const firstNft = nftsToSweep[0];

    try {
      const payload = await createPurchasePayload(firstNft);
      setState(s => ({
        ...s,
        currentPayload: payload,
        currentNft: firstNft,
      }));
    } catch (err: any) {
      // First NFT failed, record and try next
      setState(s => ({
        ...s,
        failedPurchases: [...s.failedPurchases, {
          tokenId: firstNft.nft_token_id,
          name: firstNft.name || `NFT #${firstNft.nft_token_id.slice(-8)}`,
          error: err.message,
        }],
      }));
      // Move to next NFT
      await processNextNft(0);
    }
  }, [nftsToSweep, createPurchasePayload]);

  /**
   * Process the next NFT after current one completes or fails
   */
  const processNextNft = useCallback(async (currentIndex: number) => {
    const nextIndex = currentIndex + 1;

    // Check if we're done or cancelled
    if (nextIndex >= nftsToSweep.length || state.cancelled) {
      setState(s => ({
        ...s,
        stage: 'complete',
        currentPayload: null,
        currentNft: null,
      }));
      return;
    }

    const nextNft = nftsToSweep[nextIndex];
    setState(s => ({
      ...s,
      currentIndex: nextIndex,
      currentPayload: null,
      currentNft: nextNft,
    }));

    try {
      const payload = await createPurchasePayload(nextNft);
      setState(s => ({
        ...s,
        currentPayload: payload,
      }));
    } catch (err: any) {
      // Failed to create payload for this NFT
      setState(s => ({
        ...s,
        failedPurchases: [...s.failedPurchases, {
          tokenId: nextNft.nft_token_id,
          name: nextNft.name || `NFT #${nextNft.nft_token_id.slice(-8)}`,
          error: err.message,
        }],
      }));
      // Try next NFT
      await processNextNft(nextIndex);
    }
  }, [nftsToSweep, state.cancelled, createPurchasePayload]);

  /**
   * Handle successful transaction signing
   */
  const handleSignSuccess = useCallback(async (txid?: string) => {
    const { currentIndex, currentNft } = state;

    if (currentNft) {
      setState(s => ({
        ...s,
        completedPurchases: [...s.completedPurchases, currentNft.nft_token_id],
      }));
    }

    // Process next NFT
    await processNextNft(currentIndex);
  }, [state.currentIndex, state.currentNft, processNextNft]);

  /**
   * Handle rejected or expired transaction
   */
  const handleSignRejected = useCallback(async (reason?: string) => {
    const { currentIndex, currentNft } = state;

    if (currentNft) {
      setState(s => ({
        ...s,
        failedPurchases: [...s.failedPurchases, {
          tokenId: currentNft.nft_token_id,
          name: currentNft.name || `NFT #${currentNft.nft_token_id.slice(-8)}`,
          error: reason || 'Transaction rejected or expired',
        }],
      }));
    }

    // Process next NFT
    await processNextNft(currentIndex);
  }, [state.currentIndex, state.currentNft, processNextNft]);

  /**
   * Cancel remaining purchases
   */
  const cancelRemaining = useCallback(() => {
    setState(s => ({
      ...s,
      cancelled: true,
      stage: 'complete',
      currentPayload: null,
      currentNft: null,
    }));
  }, []);

  /**
   * Reset state to idle
   */
  const reset = useCallback(() => {
    setState(initialState);
    setNftsToSweep([]);
  }, []);

  /**
   * Cancel confirmation and return to idle
   */
  const cancelConfirmation = useCallback(() => {
    reset();
  }, [reset]);

  return {
    // State
    state,
    nftsToSweep,

    // Computed
    totals: calculateSweepTotals(nftsToSweep),

    // Actions
    startConfirmation,
    startPurchasing,
    handleSignSuccess,
    handleSignRejected,
    cancelRemaining,
    cancelConfirmation,
    reset,
  };
}
